// Adapter: oversetter workerens PengestromResponse → den ekte Norge-kart-komponenten
// (../PengestromMap, ren inline-SVG). Beholder props-kontrakten {data,isLoading} slik at
// SelskapsinfoPage er uendret. Byer uten geokoding fra workeren reddes av CITY_FALLBACK.
import RealMap, { type FlowOrigin } from "../PengestromMap";
import { Skeleton } from "@/components/ui/skeleton";
import type { PengestromResponse } from "../api/types";

export interface PengestromMapProps {
  data: PengestromResponse | undefined;
  isLoading: boolean;
}

// SPEC §3.1 — poststed → [lat, lon] for de største stedene, så investor-byer workeren
// ikke fikk geokodet fortsatt plottes (det ekte kartet krever numeriske lat/lon).
const CITY_FALLBACK: Record<string, [number, number]> = {
  oslo: [59.9139, 10.7522], bergen: [60.3913, 5.3221], trondheim: [63.4305, 10.3951],
  stavanger: [58.97, 5.7331], kristiansand: [58.1467, 7.9956], tromsø: [69.6492, 18.9553],
  drammen: [59.744, 10.2045], fredrikstad: [59.2181, 10.9298], sandnes: [58.8524, 5.7352],
  bodø: [67.2804, 14.4049], ålesund: [62.4722, 6.1549], skien: [59.2096, 9.6089],
  ski: [59.7195, 10.8357], lillestrøm: [59.9559, 11.0455], moss: [59.4344, 10.6578],
  hamar: [60.7945, 11.0679], haugesund: [59.4138, 5.268], tønsberg: [59.2674, 10.4076],
};

function placeholder(text: string) {
  return (
    <div className="surface-dark relative flex h-[300px] items-center justify-center overflow-hidden rounded-[14px]">
      <span className="px-6 text-center text-[12px] text-[var(--amber)]">{text}</span>
    </div>
  );
}

export function PengestromMap({ data, isLoading }: PengestromMapProps) {
  if (isLoading) {
    return <Skeleton className="h-[300px] rounded-[14px] bg-[rgba(120,60,10,0.12)]" />;
  }
  if (!data) {
    return placeholder("Ingen pengestrøm-data.");
  }

  const origins: FlowOrigin[] = (data.nodes ?? [])
    .map((n): FlowOrigin | null => {
      let lat = n.lat;
      let lon = n.lon;
      if (lat == null || lon == null) {
        const fb = CITY_FALLBACK[(n.city ?? "").toLowerCase().trim()];
        if (fb) [lat, lon] = fb;
      }
      return lat != null && lon != null ? { sted: n.city, belop: n.amount, lat, lon } : null;
    })
    .filter((o): o is FlowOrigin => o !== null);

  const d = data.destination;
  if (data.illustrative || d?.lat == null || d?.lon == null || origins.length === 0) {
    return placeholder(
      "Illustrativt — ekte investor-geografi kobles på via worker (loan_orders → poststed)."
    );
  }

  return (
    <RealMap
      origins={origins}
      destinasjon={{ adresse: d.label ?? "Prosjekt", lat: d.lat, lon: d.lon }}
    />
  );
}
